// [Codex 2026-06-30] Foreground-only active feature usage reward pings.
import { buildAuthJsonHeaders } from "./auth-headers";
import { getRuntimeApiBase } from "./runtime-api-base";
import { useEffect } from "react";

const tickMs = 60_000;
const recentInteractionMs = 120_000;

export function useActiveFeatureUsage(enabled: boolean) {
  useEffect(() => {
    if (!enabled || typeof window === "undefined") return;
    const lastInteractionRef = { current: 0 };
    const lastWindowRef = { current: 0 };
    const surfaceRef = { current: "app-shell" };

    function recordInteraction(event: Event) {
      const now = Date.now();
      lastInteractionRef.current = now;
      if (!lastWindowRef.current) lastWindowRef.current = now;
      surfaceRef.current = surfaceFromEvent(event) ?? surfaceRef.current;
    }

    async function emitWindow() {
      const now = Date.now();
      if (!lastWindowRef.current || document.visibilityState !== "visible")
        return;
      if (
        !document.hasFocus() ||
        now - lastInteractionRef.current > recentInteractionMs
      ) {
        return;
      }
      const activeSeconds = Math.max(
        1,
        Math.min(60, Math.floor((now - lastWindowRef.current) / 1000)),
      );
      lastWindowRef.current = now;
      const endedAt = new Date(now);
      const startedAt = new Date(now - activeSeconds * 1000);
      await fetch(`${getRuntimeApiBase()}/api/usage/activity`, {
        method: "POST",
        headers: await buildAuthJsonHeaders(),
        body: JSON.stringify({
          surface: surfaceRef.current,
          action: "active_feature_window",
          startedAt: startedAt.toISOString(),
          endedAt: endedAt.toISOString(),
          activeSeconds,
          metadata: { focused: true, visibility: "visible" },
        }),
      }).catch(() => undefined);
    }

    window.addEventListener("pointerdown", recordInteraction, true);
    window.addEventListener("keydown", recordInteraction, true);
    const interval = window.setInterval(() => void emitWindow(), tickMs);
    return () => {
      window.removeEventListener("pointerdown", recordInteraction, true);
      window.removeEventListener("keydown", recordInteraction, true);
      window.clearInterval(interval);
    };
  }, [enabled]);
}

function surfaceFromEvent(event: Event): string | null {
  const target = event.target instanceof Element ? event.target : null;
  const surface = target?.closest(
    "[data-tour-target],[data-weekly-walkthrough-target]",
  );
  return (
    surface?.getAttribute("data-tour-target") ??
    surface?.getAttribute("data-weekly-walkthrough-target") ??
    null
  );
}
